/* assets/collection-cards-pro.js */
(() => {
  const ROOT_SEL = "[data-ccp]";
  const TRACK_SEL = "[data-ccp-track]";
  const TRACK_WRAP_SEL = ".ccp__track-wrap";

  const CARD_SEL = "[data-ccp-card]";
  const META_SEL = "[data-ccp-meta]";
  const PRICE_WRAP_SEL = "[data-ccp-price]";
  const RATING_WRAP_SEL = ".ccp-rating";
  const OPTS_WRAP_SEL = ".ccp-opts";

  const IMG_BOX_SEL = "[data-ccp-img-box]";
  const IMG_PRIMARY_SEL = "[data-ccp-img-primary]";
  const IMG_SECONDARY_SEL = "[data-ccp-img-secondary]";

  const SALE_BADGE_SEL = "[data-ccp-sale-badge]";
  const PRICE_RANGE_SEL = "[data-ccp-price-range]";
  const PRICE_SINGLE_SEL = "[data-ccp-price-single]";
  const PRICE_COMPARE_SEL = "[data-ccp-compare]";
  const PRICE_CURRENT_SEL = "[data-ccp-current]";

  const OPT_BTN_SEL = "[data-ccp-opt]";
  const OPT_ROW_SEL = "[data-ccp-opt-row]";
  const OPT_SCROLLER_SEL = "[data-ccp-opt-scroller]";
  const OPT_PREV_SEL = "[data-ccp-opt-prev]";
  const OPT_NEXT_SEL = "[data-ccp-opt-next]";

  const ARROW_PREV_SEL = "[data-ccp-prev]";
  const ARROW_NEXT_SEL = "[data-ccp-next]";

  const VARIANT_SCRIPT_SEL = "script[data-ccp-variant-data]";

  // Actions: view detail only
  const DETAIL_BTN_SEL = "[data-ccp-detail]";

  const stateByCard = new WeakMap();

  const clamp = (n, a, b) => Math.max(a, Math.min(b, n));

  const rafThrottle = (fn) => {
    let raf = 0;
    return (...args) => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        fn(...args);
      });
    };
  };

  const getVarStr = (el, name, fallback = "") => {
    const v = getComputedStyle(el).getPropertyValue(name).trim();
    return v || fallback;
  };

  const px = (el, prop) => {
    const v = getComputedStyle(el)[prop];
    const n = parseFloat(v || "0");
    return Number.isFinite(n) ? n : 0;
  };

  const isAutoHeight = (root) => {
    const mode = String(root?.dataset?.heightMode || "").toLowerCase();
    return mode === "auto" || root?.classList?.contains("height-auto_tallest") || root?.classList?.contains("height-auto");
  };

  const isMobileGridOneCol = (root) => {
    if (!root) return false;
    const isMobile = window.matchMedia("(max-width: 989px)").matches;
    const mode = String(root.dataset.mode || "");
    const colsM = String(root.dataset.columnsMobile || "");
    return isMobile && mode === "grid" && colsM === "1";
  };

  /* -----------------------------
    Force show/hide
  ----------------------------- */
  const setElVisible = (el, visible) => {
    if (!el) return;
    if (visible) {
      el.hidden = false;
      el.removeAttribute("hidden");
      if (el.style && el.style.display === "none") el.style.display = "";
      if (el.getAttribute("style") && el.getAttribute("style").replace(/\s/g, "").includes("display:none")) {
        el.style.display = "";
      }
    } else {
      el.hidden = true;
      el.setAttribute("hidden", "");
      if (el.style) el.style.display = "none";
    }
  };

  /* -----------------------------
    Money formatter (minimal)
  ----------------------------- */
  const formatMoney = (cents, format) => {
    let value = cents;
    if (typeof value === "string") value = value.replace(".", "");
    value = parseInt(value, 10);
    if (!Number.isFinite(value)) value = 0;

    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const match = String(format || "").match(placeholderRegex);
    if (!match) return String(value);

    const formatWithDelimiters = (amount, precision, thousands, decimal) => {
      const number = (amount / 100.0).toFixed(precision);
      const parts = number.split(".");
      const dollars = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      const centsPart = parts[1] ? decimal + parts[1] : "";
      return dollars + centsPart;
    };

    const type = match[1];
    let formatted = "";

    switch (type) {
      case "amount":
        formatted = formatWithDelimiters(value, 2, ",", ".");
        break;
      case "amount_no_decimals":
        formatted = formatWithDelimiters(value, 0, ",", ".");
        break;
      case "amount_with_comma_separator":
        formatted = formatWithDelimiters(value, 2, ".", ",");
        break;
      case "amount_no_decimals_with_comma_separator":
        formatted = formatWithDelimiters(value, 0, ".", ",");
        break;
      case "amount_with_apostrophe_separator":
        formatted = formatWithDelimiters(value, 2, "'", ".");
        break;
      default:
        formatted = formatWithDelimiters(value, 2, ",", ".");
    }

    return String(format).replace(placeholderRegex, formatted);
  };

  /* -----------------------------
    Variant data parse (옵션 상태/이미지용)
  ----------------------------- */
  const getCardState = (card) => {
    let st = stateByCard.get(card);
    if (st) return st;

    st = {
      parsed: false,
      data: null,
      map: null,
      selections: [],
      picked: [],
      displayedIdxs: [],
      defaultVariant: null
    };
    stateByCard.set(card, st);
    return st;
  };

  const uniqSorted = (arr) =>
    Array.from(new Set(arr.filter((n) => Number.isFinite(n) && n >= 0))).sort((a, b) => a - b);

  const parseCardDataIfNeeded = (card) => {
    const st = getCardState(card);
    if (st.parsed) return st;

    const script = card.querySelector(VARIANT_SCRIPT_SEL);
    if (!script) {
      st.parsed = true;
      st.data = null;
      st.map = null;
      return st;
    }

    try {
      const data = JSON.parse(script.textContent || "{}");
      st.data = data;

      const variants = Array.isArray(data.variants) ? data.variants : [];

      const map = new Map();
      for (const v of variants) {
        const key = Array.isArray(v.options) ? v.options.join("||") : "";
        if (key) map.set(key, v);
      }
      st.map = map;

      st.defaultVariant = variants.find((v) => v && v.available) || variants[0] || null;

      const optCountFromData =
        (variants[0] && Array.isArray(variants[0].options) && variants[0].options.length) ||
        (Array.isArray(data.options) && data.options.length) ||
        0;

      const rows = Array.from(card.querySelectorAll(OPT_ROW_SEL));
      let optCountFromDom = 0;
      const displayed = [];

      rows.forEach((row, fallbackIdx) => {
        const idxRaw = row.dataset.optIndex || row.getAttribute("data-opt-index") || String(fallbackIdx);
        const idx = parseInt(idxRaw, 10);
        if (Number.isFinite(idx) && idx >= 0) {
          displayed.push(idx);
          optCountFromDom = Math.max(optCountFromDom, idx + 1);
        }
      });

      const optCount = Math.max(optCountFromData, optCountFromDom, rows.length, 0);
      st.displayedIdxs = uniqSorted(displayed);

      st.selections = new Array(optCount).fill("");
      st.picked = new Array(optCount).fill(false);

      // hidden options default fill
      if (st.defaultVariant && Array.isArray(st.defaultVariant.options)) {
        st.defaultVariant.options.forEach((val, i) => {
          if (i < st.selections.length) st.selections[i] = String(val || "");
        });
      }

      // pressed state from DOM
      rows.forEach((row, fallbackIdx) => {
        const idxRaw = row.dataset.optIndex || row.getAttribute("data-opt-index") || String(fallbackIdx);
        const idx = parseInt(idxRaw, 10);
        if (!Number.isFinite(idx) || idx < 0) return;

        const pressed = row.querySelector(`${OPT_BTN_SEL}[aria-pressed="true"]`);
        if (!pressed) return;

        const val = pressed.dataset.optValue || "";
        while (st.selections.length <= idx) st.selections.push("");
        while (st.picked.length <= idx) st.picked.push(false);

        st.selections[idx] = val;
        st.picked[idx] = true;
      });

      st.parsed = true;
    } catch (_) {
      st.parsed = true;
      st.data = null;
      st.map = null;
    }

    return st;
  };

  const allDisplayedPicked = (st) => {
    const idxs = Array.isArray(st.displayedIdxs) ? st.displayedIdxs : [];
    if (!idxs.length) return true;
    return idxs.every((i) => !!st.picked[i]);
  };

  /* -----------------------------
    Card image update only
  ----------------------------- */
  const updateCardImageOnly = (card, variant) => {
    const img = card.querySelector(IMG_PRIMARY_SEL);
    if (!img || !variant) return;

    const src480 = variant.img480 || variant.img320 || "";
    if (!src480) return;

    img.src = src480;

    const srcset = [
      variant.img320 ? `${variant.img320} 320w` : "",
      variant.img480 ? `${variant.img480} 480w` : "",
      variant.img720 ? `${variant.img720} 720w` : "",
      variant.img960 ? `${variant.img960} 960w` : ""
    ]
      .filter(Boolean)
      .join(", ");

    if (srcset) img.setAttribute("srcset", srcset);
  };

  /* -----------------------------
    Price UI (final rules)
  ----------------------------- */
  const setSaleBadgeVisible = (badgeEl, visible) => {
    if (!badgeEl) return;
    if (visible) {
      badgeEl.hidden = false;
      if (badgeEl.style && badgeEl.style.display === "none") badgeEl.style.display = "";
      if (badgeEl.getAttribute("style") && badgeEl.getAttribute("style").replace(/\s/g, "") === "display:none;") {
        badgeEl.removeAttribute("style");
      }
    } else {
      badgeEl.hidden = true;
      badgeEl.style.display = "none";
    }
  };

  const showSinglePrice = (card, price, compareAt, moneyFormat) => {
    const range = card.querySelector(PRICE_RANGE_SEL);
    const single = card.querySelector(PRICE_SINGLE_SEL);

    const fmt = moneyFormat || "{{ amount }}";
    const p = price || 0;
    const c = compareAt || 0;

    const moneyPrice = formatMoney(p, fmt);
    const moneyCompare = c > 0 ? formatMoney(c, fmt) : "";

    const onSale = c > p && c > 0;

    const container = single || range;

    if (single) {
      setElVisible(range, false);
      setElVisible(single, true);
    } else {
      setElVisible(range, true);
    }

    if (!container) return;

    const currentEl = container.querySelector(PRICE_CURRENT_SEL) || container.querySelector(".ccp-price__current");
    const compareEl = container.querySelector(PRICE_COMPARE_SEL);
    const saleBadge = container.querySelector(SALE_BADGE_SEL);

    if (currentEl) currentEl.textContent = moneyPrice;

    if (compareEl) {
      if (onSale) {
        compareEl.textContent = moneyCompare;
        compareEl.hidden = false;
      } else {
        compareEl.textContent = "";
        compareEl.hidden = true;
      }
    } else {
      const otherCompare = card.querySelector(PRICE_COMPARE_SEL);
      if (otherCompare && otherCompare !== compareEl) {
        otherCompare.textContent = "";
        otherCompare.hidden = true;
      }
    }

    if (saleBadge) {
      if (onSale) {
        const pct = Math.ceil(((c - p) * 100) / c);
        saleBadge.textContent = `${pct}%off`;
        setSaleBadgeVisible(saleBadge, true);
      } else {
        saleBadge.textContent = "";
        setSaleBadgeVisible(saleBadge, false);
      }
    } else {
      const otherBadge = card.querySelector(SALE_BADGE_SEL);
      if (otherBadge && otherBadge !== saleBadge) {
        otherBadge.textContent = "";
        setSaleBadgeVisible(otherBadge, false);
      }
    }
  };

  const showPriceRange = (card, minPrice, maxPrice, moneyFormat) => {
    const range = card.querySelector(PRICE_RANGE_SEL);
    const single = card.querySelector(PRICE_SINGLE_SEL);

    setElVisible(range, true);
    setElVisible(single, false);

    if (single) {
      const saleBadge = single.querySelector(SALE_BADGE_SEL);
      const compareEl = single.querySelector(PRICE_COMPARE_SEL);
      if (saleBadge) {
        saleBadge.textContent = "";
        setSaleBadgeVisible(saleBadge, false);
      }
      if (compareEl) {
        compareEl.hidden = true;
        compareEl.textContent = "";
      }
    }
    if (range) {
      const compareEl = range.querySelector(PRICE_COMPARE_SEL);
      const saleBadge = range.querySelector(SALE_BADGE_SEL);
      if (compareEl) {
        compareEl.hidden = true;
        compareEl.textContent = "";
      }
      if (saleBadge) {
        saleBadge.textContent = "";
        setSaleBadgeVisible(saleBadge, false);
      }

      const fmt = moneyFormat || "{{ amount }}";
      const min = formatMoney(minPrice || 0, fmt);
      const max = formatMoney(maxPrice || 0, fmt);

      const current = range.querySelector(".ccp-price__current") || range.querySelector(PRICE_CURRENT_SEL);
      if (current) current.textContent = `${min} - ${max}`;
    }
  };

  const initPriceUI = (card, data, defaultVariant) => {
    if (!data) return;

    const variants = Array.isArray(data.variants) ? data.variants : [];
    const fmt = data.moneyFormat || "{{ amount }}";

    if (variants.length <= 1) {
      const v = defaultVariant || variants[0] || null;
      const price = v ? (v.price ?? 0) : (data.priceMin ?? 0);
      const compareAt = v ? (v.compareAt || 0) : 0;
      showSinglePrice(card, price, compareAt, fmt);
      return;
    }

    let minP = Infinity;
    let maxP = -Infinity;
    let firstPrice = variants[0]?.price ?? 0;
    let allSamePrice = true;

    const prices = [];
    const compareCandidates = [];

    for (const v of variants) {
      const p = v?.price ?? 0;
      const c = v?.compareAt || 0;

      prices.push(p);

      if (p !== firstPrice) allSamePrice = false;
      if (p < minP) minP = p;
      if (p > maxP) maxP = p;

      if (c > p && c > 0) compareCandidates.push(c);
    }

    if (!allSamePrice || minP !== maxP) {
      const minPrice = Number.isFinite(data.priceMin) ? data.priceMin : (Number.isFinite(minP) ? minP : 0);
      const maxPrice = Number.isFinite(data.priceMax) ? data.priceMax : (Number.isFinite(maxP) ? maxP : minPrice);
      showPriceRange(card, minPrice, maxPrice, fmt);
      return;
    }

    const samePrice = prices[0] ?? 0;

    if (compareCandidates.length) {
      const safeCompareAt = Math.min(...compareCandidates);
      showSinglePrice(card, samePrice, safeCompareAt, fmt);
      return;
    }

    showSinglePrice(card, samePrice, 0, fmt);
  };

  /* -----------------------------
    Measurements: card sizing + secondary image flag
  ----------------------------- */
  const applySecondaryFlag = (card) => {
    const hasSecondary = !!card.querySelector(IMG_SECONDARY_SEL);
    card.classList.toggle("has-secondary", hasSecondary);
  };

  const measureCards = (root) => {
    const cards = root.querySelectorAll(CARD_SEL);

    cards.forEach((card) => {
      const w = card.getBoundingClientRect().width;
      if (w) card.style.setProperty("--ccp-card-w", `${w}px`);

      const box = card.querySelector(IMG_BOX_SEL);
      if (box) {
        box.style.removeProperty("width");
        box.style.removeProperty("height");
        box.style.removeProperty("max-width");
        box.style.removeProperty("aspect-ratio");
      }

      card.querySelectorAll("img").forEach((img) => img.setAttribute("draggable", "false"));
      applySecondaryFlag(card);

      const detail = card.querySelector(DETAIL_BTN_SEL);
      card.classList.toggle("ccp-only-detail", !!detail);
    });
  };

  /* -----------------------------
    Normalize option rows count (리뷰/금액 행 위치 고정)
    중요: 서버(Liquid) placeholder + JS placeholder가 중복되면 정렬이 깨짐.
         그래서 data-ccp-opt-row 없는 row는 전부 제거 후 JS 기준으로만 다시 채움.
  ----------------------------- */
  const normalizeOptionRows = (root) => {
    const cards = Array.from(root.querySelectorAll(CARD_SEL));
    if (!cards.length) return;

    // 1) remove ALL placeholder-like rows (no data-ccp-opt-row)
    cards.forEach((card) => {
      const wrap = card.querySelector(OPTS_WRAP_SEL);
      if (!wrap) return;

      // remove any row without data attr (server placeholder or previous JS placeholder)
      wrap.querySelectorAll(".ccp-opts__row:not([data-ccp-opt-row])").forEach((n) => n.remove());
    });

    // 2) find max real option rows
    let maxRows = 0;
    const counts = new Map();

    for (const card of cards) {
      const wrap = card.querySelector(OPTS_WRAP_SEL);
      if (!wrap) {
        counts.set(card, 0);
        continue;
      }
      const c = wrap.querySelectorAll(OPT_ROW_SEL).length;
      counts.set(card, c);
      if (c > maxRows) maxRows = c;
    }

    if (maxRows <= 0) return;

    // 3) add placeholders to match maxRows
    for (const card of cards) {
      const wrap = card.querySelector(OPTS_WRAP_SEL);
      if (!wrap) continue;

      const c = counts.get(card) || 0;
      const need = Math.max(0, maxRows - c);
      if (!need) continue;

      for (let i = 0; i < need; i++) {
        const row = document.createElement("div");
        row.className = "ccp-opts__row is-placeholder";
        row.setAttribute("aria-hidden", "true");

        // no data-ccp-opt-row / no data-ccp-opt-scroller on purpose
        const scroller = document.createElement("div");
        scroller.className = "ccp-opts__scroller";
        row.appendChild(scroller);

        wrap.appendChild(row);
      }
    }
  };

  /* -----------------------------
    Slider: arrows, snap, drag
  ----------------------------- */
  const getGapPx = (track) => {
    const cs = getComputedStyle(track);
    const gap = parseFloat(cs.columnGap || cs.gap || "0");
    return Number.isFinite(gap) ? gap : 0;
  };

  const getSlides = (track) => Array.from(track.children || []).filter((n) => n && n.nodeType === 1);

  const getNearestIndex = (track) => {
    const slides = getSlides(track);
    if (!slides.length) return 0;

    const padL = px(track, "paddingLeft");
    const x = track.scrollLeft;
    let bestIdx = 0;
    let bestD = Infinity;

    for (let i = 0; i < slides.length; i++) {
      const left = slides[i].offsetLeft - padL;
      const d = Math.abs(left - x);
      if (d < bestD) {
        bestD = d;
        bestIdx = i;
      }
    }
    return bestIdx;
  };

  const snapToIndex = (track, idx) => {
    const slides = getSlides(track);
    if (!slides.length) return;

    const padL = px(track, "paddingLeft");
    const targetRaw = slides[clamp(idx, 0, slides.length - 1)].offsetLeft - padL;
    const maxScroll = track.scrollWidth - track.clientWidth;
    const target = clamp(targetRaw, 0, Math.max(0, maxScroll));

    track.scrollTo({ left: target, behavior: "smooth" });
  };

  const snapToNearest = (track) => snapToIndex(track, getNearestIndex(track));

  const scrollOneCard = (track, dir) => {
    const idx = getNearestIndex(track);
    snapToIndex(track, idx + dir);

    const gap = getGapPx(track);
    if (dir > 0) track.scrollLeft += gap * 0.001;
  };

  const updateSliderArrows = (root) => {
    if (!root.classList.contains("mode-slider")) return;

    const track = root.querySelector(TRACK_SEL);
    if (!track) return;

    const prev = root.querySelector(ARROW_PREV_SEL);
    const next = root.querySelector(ARROW_NEXT_SEL);
    if (!prev && !next) return;

    const maxScroll = track.scrollWidth - track.clientWidth;
    const x = track.scrollLeft;

    const canPrev = x > 2;
    const canNext = x < maxScroll - 2;

    if (prev) prev.disabled = !canPrev;
    if (next) next.disabled = !canNext;
  };

  const stabilizeSliderHeight = (root) => {
    if (!root.classList.contains("mode-slider")) return;

    const track = root.querySelector(TRACK_SEL);
    const wrap = root.querySelector(TRACK_WRAP_SEL);
    if (!track || !wrap) return;

    let maxH = 0;
    const items = getSlides(track);

    for (const li of items) {
      const h = li.getBoundingClientRect().height;
      if (h > maxH) maxH = h;
    }

    if (maxH > 0) wrap.style.setProperty("--ccp-track-min-h", `${Math.ceil(maxH)}px`);
    else wrap.style.removeProperty("--ccp-track-min-h");
  };

  const setupTrackDrag = (root) => {
    const track = root.querySelector(TRACK_SEL);
    if (!track) return;

    let isDown = false;
    let startX = 0;
    let startY = 0;
    let startScroll = 0;
    let movedX = false;
    let recentDrag = false;

    const clearRecent = () => (recentDrag = false);

    // prevent link click after drag
    root.addEventListener(
      "click",
      (e) => {
        if (!recentDrag) return;
        const link = e.target.closest("a[href]");
        if (!link) return;
        e.preventDefault();
        e.stopPropagation();
      },
      true
    );

    track.addEventListener("dragstart", (e) => e.preventDefault());

    const onDown = (e) => {
      if (!root.classList.contains("mode-slider")) return;
      if (e.pointerType === "mouse" && e.button !== 0) return;

      // IMPORTANT:
      // If the pointerdown happens inside option scroller area, do NOT start outer track drag.
      // This keeps "이형상품 옵션 슬라이드" working when options overflow the card width.
      if (e.target.closest(OPTS_WRAP_SEL) || e.target.closest(OPT_SCROLLER_SEL)) return;

      const interactive = e.target.closest("button, a, input, select, textarea, [role='button']");
      if (interactive) return;

      isDown = true;
      movedX = false;
      startX = e.clientX;
      startY = e.clientY;
      startScroll = track.scrollLeft;

      try {
        track.setPointerCapture(e.pointerId);
      } catch (_) {}

      track.classList.add("is-dragging");
      root.classList.add("ccp-is-dragging");
    };

    const onMove = (e) => {
      if (!isDown) return;

      const dx = e.clientX - startX;
      const dy = e.clientY - startY;

      if (!movedX && Math.abs(dy) > 10 && Math.abs(dy) > Math.abs(dx) + 6) {
        isDown = false;
        track.classList.remove("is-dragging");
        root.classList.remove("ccp-is-dragging");
        return;
      }

      if (Math.abs(dx) > 8) movedX = true;
      if (!movedX) return;

      e.preventDefault();
      track.scrollLeft = startScroll - dx;
    };

    const onEnd = () => {
      if (!isDown) return;
      isDown = false;

      track.classList.remove("is-dragging");
      root.classList.remove("ccp-is-dragging");

      if (movedX) {
        recentDrag = true;
        setTimeout(clearRecent, 220);
        snapToNearest(track);
      }
    };

    track.addEventListener("pointerdown", onDown, { passive: true });
    track.addEventListener("pointermove", onMove, { passive: false });
    track.addEventListener("pointerup", onEnd, { passive: true });
    track.addEventListener("pointercancel", onEnd, { passive: true });
    track.addEventListener("pointerleave", onEnd, { passive: true });
  };

  /* -----------------------------
    Options row overflow + arrows + alignment
  ----------------------------- */
  const updateOptRow = (row) => {
    const scroller = row.querySelector(OPT_SCROLLER_SEL);
    if (!scroller) return;

    const root = row.closest(ROOT_SEL);
    const align = root ? getVarStr(root, "--ccp-text-align", "left") : "left";

    const overflow = scroller.scrollWidth > scroller.clientWidth + 2;
    row.classList.toggle("is-overflowing", overflow);

    // enforce snap behavior for overflow case (in case any inline overrides happened elsewhere)
    if (overflow) {
      if (scroller.style.scrollSnapType === "none") scroller.style.scrollSnapType = "x mandatory";
      scroller.style.justifyContent = "";
    } else {
      // no overflow: align chips visually
      if (align === "center") scroller.style.justifyContent = "center";
      else if (align === "right") scroller.style.justifyContent = "flex-end";
      else scroller.style.justifyContent = "flex-start";
    }

    const prev = row.querySelector(OPT_PREV_SEL);
    const next = row.querySelector(OPT_NEXT_SEL);
    if (!prev && !next) return;

    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    const x = scroller.scrollLeft;

    if (prev) prev.disabled = x <= 2;
    if (next) next.disabled = x >= maxScroll - 2;
  };

  const scrollOptRowOne = (row, dir) => {
    const scroller = row.querySelector(OPT_SCROLLER_SEL);
    if (!scroller) return;

    const items = Array.from(scroller.querySelectorAll(OPT_BTN_SEL));
    if (!items.length) return;

    const padL = px(scroller, "paddingLeft");
    const x = scroller.scrollLeft;

    let target = null;

    if (dir > 0) {
      for (const it of items) {
        const left = it.offsetLeft - padL;
        if (left > x + 2) {
          target = left;
          break;
        }
      }
      if (target == null) target = scroller.scrollWidth;
    } else {
      for (let i = items.length - 1; i >= 0; i--) {
        const left = items[i].offsetLeft - padL;
        if (left < x - 2) {
          target = left;
          break;
        }
      }
      if (target == null) target = 0;
    }

    const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    const to = clamp(target, 0, maxScroll);

    scroller.scrollTo({ left: to, behavior: "smooth" });

    // update arrows state soon (scroll event also handles it, but this makes it feel immediate)
    requestAnimationFrame(() => updateOptRow(row));
  };

  /* -----------------------------
    Auto height: uniform card height + rating/price row alignment
  ----------------------------- */
  const applyUniformCardHeight = (root) => {
    if (!isAutoHeight(root)) {
      root.style.removeProperty("--ccp-card-uniform-h");
      return;
    }

    const cards = Array.from(root.querySelectorAll(CARD_SEL));
    if (!cards.length) return;

    let maxH = 0;
    for (const card of cards) {
      const h = card.getBoundingClientRect().height;
      if (h > maxH) maxH = h;
    }

    if (maxH > 0) root.style.setProperty("--ccp-card-uniform-h", `${Math.ceil(maxH)}px`);
  };

  const alignRatingAndPriceRows = (root) => {
    const cards = Array.from(root.querySelectorAll(CARD_SEL));
    if (!cards.length) return;

    const shouldAlign = isAutoHeight(root) && !isMobileGridOneCol(root);

    // reset
    cards.forEach((card) => {
      const r = card.querySelector(RATING_WRAP_SEL);
      const p = card.querySelector(PRICE_WRAP_SEL);
      if (r) r.style.removeProperty("margin-top");
      if (p) p.style.removeProperty("margin-top");
    });

    if (!shouldAlign) return;

    // align rating
    {
      const measures = [];
      let maxBefore = 0;

      for (const card of cards) {
        const meta = card.querySelector(META_SEL);
        const el = card.querySelector(RATING_WRAP_SEL);
        if (!meta || !el) continue;

        const before = el.getBoundingClientRect().top - meta.getBoundingClientRect().top;
        const baseMt = px(el, "marginTop");

        measures.push({ el, before, baseMt });
        if (before > maxBefore) maxBefore = before;
      }

      for (const m of measures) {
        const push = Math.max(0, maxBefore - m.before);
        m.el.style.marginTop = `${Math.ceil(m.baseMt + push)}px`;
      }
    }

    // align price (after rating margin applied)
    {
      const measures = [];
      let maxBefore = 0;

      for (const card of cards) {
        const meta = card.querySelector(META_SEL);
        const el = card.querySelector(PRICE_WRAP_SEL);
        if (!meta || !el) continue;

        const before = el.getBoundingClientRect().top - meta.getBoundingClientRect().top;
        const baseMt = px(el, "marginTop");

        measures.push({ el, before, baseMt });
        if (before > maxBefore) maxBefore = before;
      }

      for (const m of measures) {
        const push = Math.max(0, maxBefore - m.before);
        m.el.style.marginTop = `${Math.ceil(m.baseMt + push)}px`;
      }
    }
  };

  const stabilizeHeights = (root) => {
    if (!root) return;

    if (isAutoHeight(root)) {
      applyUniformCardHeight(root);
      alignRatingAndPriceRows(root);

      if (root.classList.contains("mode-slider")) {
        const wrap = root.querySelector(TRACK_WRAP_SEL);
        const h = root.style.getPropertyValue("--ccp-card-uniform-h").trim();
        if (wrap && h) wrap.style.setProperty("--ccp-track-min-h", h);
        else stabilizeSliderHeight(root);
      }
      return;
    }

    // in non-auto mode: slider min-height still needed
    if (root.classList.contains("mode-slider")) stabilizeSliderHeight(root);
  };

  /* -----------------------------
    Init cards
  ----------------------------- */
  const initCards = (root) => {
    const cards = root.querySelectorAll(CARD_SEL);

    cards.forEach((card) => {
      const st = parseCardDataIfNeeded(card);
      if (!st || !st.parsed || !st.data) return;

      // price UI: one-time init (no change on option click)
      initPriceUI(card, st.data, st.defaultVariant);
    });
  };

  /* -----------------------------
    Init one section
  ----------------------------- */
  const initSection = (root) => {
    if (!root || root.__ccpInited) return;
    root.__ccpInited = true;

    const track = root.querySelector(TRACK_SEL);
    if (!track) return;

    const relayout = rafThrottle(() => {
      measureCards(root);
      normalizeOptionRows(root);
      root.querySelectorAll(OPT_ROW_SEL).forEach(updateOptRow);
      updateSliderArrows(root);
      stabilizeHeights(root);
    });

    if (root.classList.contains("mode-slider")) setupTrackDrag(root);

    root.addEventListener("click", (e) => {
      const prevArrow = e.target.closest(ARROW_PREV_SEL);
      const nextArrow = e.target.closest(ARROW_NEXT_SEL);
      if (prevArrow || nextArrow) {
        e.preventDefault();
        scrollOneCard(track, prevArrow ? -1 : 1);
        return;
      }

      const optPrev = e.target.closest(OPT_PREV_SEL);
      const optNext = e.target.closest(OPT_NEXT_SEL);
      if (optPrev || optNext) {
        e.preventDefault();
        const row = e.target.closest(OPT_ROW_SEL);
        if (row) {
          scrollOptRowOne(row, optPrev ? -1 : 1);
          // no global relayout needed here, but keep arrows updated
          requestAnimationFrame(() => updateOptRow(row));
        }
        return;
      }

      const detailBtn = e.target.closest(DETAIL_BTN_SEL);
      if (detailBtn) return;

      const optBtn = e.target.closest(OPT_BTN_SEL);
      if (optBtn) {
        e.preventDefault();

        const card = optBtn.closest(CARD_SEL);
        if (!card) return;

        const row = optBtn.closest(OPT_ROW_SEL);
        if (row) {
          const btns = row.querySelectorAll(OPT_BTN_SEL);
          btns.forEach((b) => b.setAttribute("aria-pressed", b === optBtn ? "true" : "false"));

          // update overflow/arrow state first (layout can change)
          updateOptRow(row);
        }

        const optIndex = parseInt(
          optBtn.dataset.optIndex || (row && (row.dataset.optIndex || row.getAttribute("data-opt-index"))) || "0",
          10
        );
        const optValue = optBtn.dataset.optValue || "";

        const st = parseCardDataIfNeeded(card);
        if (!st || !st.data || !st.map) return;

        if (!Number.isFinite(optIndex) || optIndex < 0) return;

        while (st.selections.length <= optIndex) st.selections.push("");
        while (st.picked.length <= optIndex) st.picked.push(false);

        st.selections[optIndex] = optValue;
        st.picked[optIndex] = true;

        // image change only when all displayed options are picked
        if (allDisplayedPicked(st)) {
          const key = st.selections.join("||");
          const variant = st.map.get(key);
          if (variant) updateCardImageOnly(card, variant);
        }

        // IMPORTANT: after option UI/layout stabilized, then do height/row alignment
        requestAnimationFrame(() => stabilizeHeights(root));
      }
    });

    if (window.matchMedia("(hover:hover) and (pointer:fine)").matches) {
      root.addEventListener("mouseover", (e) => {
        const btn = e.target.closest(OPT_BTN_SEL);
        if (!btn) return;
        const row = btn.closest(OPT_ROW_SEL);
        if (!row) return;
        const val = btn.dataset.optValue || "";
        row.querySelectorAll(OPT_BTN_SEL).forEach((b) => b.classList.toggle("is-hover", b.dataset.optValue === val));
      });

      root.addEventListener("mouseout", (e) => {
        const btn = e.target.closest(OPT_BTN_SEL);
        if (!btn) return;
        const row = btn.closest(OPT_ROW_SEL);
        if (!row) return;
        row.querySelectorAll(OPT_BTN_SEL).forEach((b) => b.classList.remove("is-hover"));
      });
    }

    const onTrackScroll = rafThrottle(() => updateSliderArrows(root));
    track.addEventListener("scroll", onTrackScroll, { passive: true });

    // bind option scroller scroll listeners (only real scrollers with data-ccp-opt-scroller)
    root.querySelectorAll(OPT_SCROLLER_SEL).forEach((scroller) => {
      const row = scroller.closest(OPT_ROW_SEL);
      if (!row) return;
      const onOptScroll = rafThrottle(() => updateOptRow(row));
      scroller.addEventListener("scroll", onOptScroll, { passive: true });
    });

    // initial layout
    measureCards(root);
    normalizeOptionRows(root);
    initCards(root);
    root.querySelectorAll(OPT_ROW_SEL).forEach(updateOptRow);
    updateSliderArrows(root);
    stabilizeHeights(root);

    // image load can change card height
    root.querySelectorAll("img").forEach((img) => {
      img.addEventListener(
        "load",
        () => {
          relayout();
        },
        { once: true, passive: true }
      );
    });

    // resize observer for all recalcs (order is critical)
    const ro = new ResizeObserver(() => relayout());
    ro.observe(root);
    root.__ccpRO = ro;
  };

  /* -----------------------------
    Lazy init
  ----------------------------- */
  const boot = () => {
    const roots = Array.from(document.querySelectorAll(ROOT_SEL));
    if (!roots.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((ent) => {
          if (!ent.isIntersecting) return;
          initSection(ent.target);
          io.unobserve(ent.target);
        });
      },
      { rootMargin: "250px 0px", threshold: 0.01 }
    );

    roots.forEach((r) => io.observe(r));
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  document.addEventListener("shopify:section:load", (e) => {
    const root = e.target.querySelector(ROOT_SEL);
    if (root) initSection(root);
  });

  document.addEventListener("shopify:section:unload", (e) => {
    const root = e.target.querySelector(ROOT_SEL);
    if (!root) return;
    if (root.__ccpRO) {
      try {
        root.__ccpRO.disconnect();
      } catch (_) {}
      root.__ccpRO = null;
    }
  });
})();
